<template>
<LayoutSearchFieldVertical>
  <template #default v-if="!hideKeyword">
    <div class="search-image-select">
      <div class="jps-sis-thumb jps-sis-thumb-body">
        <div class="jps-sis-type">
          {{ lss('画像の中身を検索', 'Search image within') }}
        </div>
        <form
          class="search-keyword-field"
          @submit.prevent.stop="searchWithKeyword"
        >
          <input
            class="jps-input jps-keyword-input"
            type="text"
            v-model="keyword"
            :placeholder="
              lss(
                'モチーフやシチュエーションを言葉で検索',
                'Enter words for motif or situation',
              )
            "
            @keyup.prevent.esc="keyword = ''"
          />
          <UiButtonIcon
            styled="secondary"
            tag="button"
            type="submit"
            icon-size="size-5"
            icon="mdi/magnify"
          />
        </form>
        <div>
          <div class="keyword-samples">
            <span>{{ lss('たとえば... ', 'Example:') }}</span>
            <BaseLink :to="searchTo({ csid: systemCrossSearchId(), text2image: '馬に乗った人' })"
              >{{ lss('馬に乗った人', 'person on horseback') }}</BaseLink>
            <BaseLink
              :to="searchTo({csid: systemCrossSearchId(), text2image: '田園を描いた印象派の絵画'})"
              >{{ lss('田園を描いた印象派の絵画', 'impressionist paintings depicting the countryside') }}</BaseLink>
            <BaseLink
              :to="searchTo({csid: systemCrossSearchId(), text2image: '海に沈む夕日'})"
              >{{ lss('海に沈む夕日', 'sunsets over the ocean') }}</BaseLink>
            <BaseLink :to="searchTo({csid: systemCrossSearchId(), text2image: 'ネコ科の生き物'})"
              >{{ lss('ネコ科の生き物', 'feline creatures') }}</BaseLink>
          </div>
        </div>
      </div>
    </div>
  </template>

  <template #nav v-if="!hideImage">
    <div class="jps-sis-thumb jps-sis-thumb-nav">
      <div class="jps-sis-type">
        {{ ls(dapLangGlobal.searchImage) }}
      </div>
      <div class="jps-level">
        <div class="jps-sis-message">
          <div class="jps-sis-message-title">
            <span
              class="icon"
              :class="{
                'hina-logo-icon': isHina,
                'jps-logo-icon': isJps,
              }" />
            {{
              lst(dapLangGlobal.searchImageWithImage, ls(dapLangGlobal.serviceName))
            }}
          </div>

          <div class="jps-sis-message-button">
            <button
              class="jps-round-button jps-grey search-field-image-button"
              type="button"
              @click.prevent.stop="openSelectItemModal()"
            >
              <BaseIcon name="mdi/image-search" size="size-6" />
              {{ lss('画像を指定', 'Select image') }}
            </button>
          </div>
        </div>

        <div class="jps-sis-message">
          <div class="jps-sis-message-title">
            <BaseIcon
              class="jps-hastext-dark icon"
              name="mdi/cloud-upload-outline"
              size="size-2"
            />
            {{ lss('自分の持っている画像で検索', 'Search with your image') }}
          </div>

          <div class="jps-sis-message-button">
            <button
              class="jps-round-button jps-grey search-field-image-button"
              type="button"
              @click.prevent="openUploadModal()"
            >
              <BaseIcon name="mdi/image-search" size="size-6" />
              {{ lss('画像をアップロード', 'Upload image') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <UiItemSelectorModal ref="itemSelectorModal" />
    <SearchFieldImageFileModal ref="searchFieldImageFileModal" />
  </template>
</LayoutSearchFieldVertical>
</template>

<script lang="ts" setup>
import { useSearchFieldBase } from '~/components/search/search-field/useBaseSearchField'
import type { SearchFieldBaseProps } from '~/components/search/search-field/useBaseSearchField'
import type { SearchFieldImageFileModal, UiItemSelectorModal } from '#components'
import { dapLangGlobal } from '#imports'

const { lss, lst, ls } = useI18n()
const { getSearchCs } = useItem()
const { isJps, isHina } = useServiceType()
const dataStore = useDataStore()

interface Props extends SearchFieldBaseProps {
}
const props = withDefaults(defineProps<Props>(), {
})

const { facet, excludeDisplay, facetNum } = useSearchFieldBase(props)

const itemSelectorModal = ref<InstanceType<typeof UiItemSelectorModal>>()
const openSelectItemModal = async () => {
  try {
    const cs: CustomSearchDefinition = clone(systemCrossSearchCs())
    cs.searchFields = null
    const ss: SearchStore<Item> = useSearch<Item>(
      cs,
      getSearchCs(systemCrossSearchCs(), false),
      {
        queryRewriter: (q: SearchQuery) => {
          q.exists = ['common.thumbnailUrl']
          return q
        }
      }
    )
    ss.state.keyword = clone(props.ss.state.keyword)
    const item = await itemSelectorModal.value.openAwait({
      message: {
        ja: '検索したいサムネイルを選んで下さい',
        en: 'Select Thumbnail to search with',
      },
      ss: ss,
    })
    if (!item) return
    dataStore.put('item', item.id, item)
    props.ss.state.keyword = []
    props.ss.state.image = [item.id]
    await props.ss.execute()
  } catch (cancel) {}
}

const searchFieldImageFileModal = ref<InstanceType<typeof SearchFieldImageFileModal>>()
const openUploadModal = async () => {
  try {
    const result = await searchFieldImageFileModal.value.openAwait()
    if (!result) return
    props.ss.state.image = [result.id]
    await props.ss.execute()
  } catch (cancel) {}
}

const isImageSearch = computed(() => {
  return props.ss.state.searchdef?.keywordFields.includes('text2image')
})

const hideKeyword = computed(() => {
  return (
    (props.ss.state.image && props.ss.state.image.length) || isImageSearch.value
  )
})

const hideImage = computed(() => {
  return props.ss.state.text2image
})

const keyword: Ref<string> = ref('')

const searchWithKeyword = () => {
  props.ss.state.text2image = keyword.value
  props.ss.state.from = 0
  props.ss.execute()
}

onMounted(() => {
  props.ss.addQueryListenr(() => {
    keyword.value = props.ss.state.text2image
  })
  keyword.value = props.ss.state.text2image
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.search-image-select {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.jps-sis-thumb {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.jps-sis-thumb-body {
  padding-left: 0.5rem;
}

.jps-sis-thumb-nav {
  padding: 0.5rem 0.5rem;
}

  .search-keyword-field {
    width: 100%;
    @include is-tablet {
      width: calc(0.25rem * 96);
    }
  }

  .jps-sis-type {
    font-size: 0.875rem;
    font-weight: 700;
    min-width: 10.5rem;
  }

  .jps-level {
    display: flex;
    gap: 0.5rem;
    @include is-mobile() {
      flex-direction: column;
    }
  }

  .jps-sis-message {
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 4px 8px;
    flex-wrap: wrap;
  }

  .jps-sis-message-title {
    display: flex;
    gap: 8px;
    align-items: center;

    .icon {
      flex-shrink: 0;
    }
  }

  .jps-hastext-dark {
    color: $jps-gray-600;
  }
  .keyword-samples {
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.375rem 1rem;
    button,
    a {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .search-field-image-button {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    line-height: 1;
  }
</style>
